<form #personForm="ngForm" autocomplete="off" (ngSubmit)="save(personForm)">
  <div class="modal-body">
    <p>Create a new person record. The system assigns a new EIN.</p>
    @if (error()) {
      <p class="alert alert-danger" role="alert">{{ error() }}</p>
    }
    @if (!codes()) {
      <button type="button" class="btn btn-outline-primary" (click)="loadCodes()">Reload Identity Choices</button>
    }
    <div formLevelValidationErrors [errorEmitter]="errorEmitter"></div>
    <fieldset [disabled]="busy()">
      <div class="row g-3">
        <div class="col-md-6">
          <label inputRequired for="workforce-person-firstName" class="form-label">First Name</label>
          <input
            id="workforce-person-firstName"
            name="firstName"
            class="form-control"
            type="text"
            #firstName="ngModel"
            [ngModel]="draft().firstName"
            (ngModelChange)="update('firstName', $event)"
          />
          <div validationErrors [for]="firstName.control" [errorEmitter]="errorEmitter"></div>
        </div>
        <div class="col-md-6">
          <label for="workforce-person-middleName" class="form-label">Middle Name (optional)</label>
          <input
            id="workforce-person-middleName"
            name="middleName"
            class="form-control"
            type="text"
            #middleName="ngModel"
            [ngModel]="draft().middleName"
            (ngModelChange)="update('middleName', $event)"
          />
          <div validationErrors [for]="middleName.control" [errorEmitter]="errorEmitter"></div>
        </div>
        <div class="col-md-6">
          <label inputRequired for="workforce-person-lastName" class="form-label">Last Name</label>
          <input
            id="workforce-person-lastName"
            name="lastName"
            class="form-control"
            type="text"
            #lastName="ngModel"
            [ngModel]="draft().lastName"
            (ngModelChange)="update('lastName', $event)"
          />
          <div validationErrors [for]="lastName.control" [errorEmitter]="errorEmitter"></div>
        </div>
        <div class="col-md-6">
          <label inputRequired for="workforce-person-ssn" class="form-label">Social Security Number</label>
          <input
            id="workforce-person-ssn"
            name="ssn"
            class="form-control"
            type="text"
            inputmode="numeric"
            mask="000-00-0000"
            [validation]="false"
            obscure
            #ssn="ngModel"
            [ngModel]="draft().ssn"
            (ngModelChange)="update('ssn', $event)"
          />
          <div validationErrors [for]="ssn.control" [errorEmitter]="errorEmitter"></div>
        </div>
        <div class="col-md-6">
          <label inputRequired for="date-input-dateOfBirth" class="form-label">Date of Birth</label>
          <cert-date-input
            name="dateOfBirth"
            [propertyName]="'dateOfBirth'"
            [model]="dateOfBirth"
            (modelChange)="dateOfBirth = $event; clearMatches()"
          />
        </div>
        <div class="col-md-6">
          <label inputRequired for="workforce-person-genderCodeId" class="form-label">Gender</label>
          <ng-select
            labelForId="workforce-person-genderCodeId"
            [readonly]="busy()"
            name="genderCodeId"
            #genderCodeId="ngModel"
            [closeOnSelect]="true"
            [multiple]="false"
            [ngModel]="draft().genderCodeId"
            (ngModelChange)="update('genderCodeId', $event)"
          >
            @for (option of codes()?.genderCodes ?? []; track option.genderCodeId) {
              <ng-option [value]="option.genderCodeId">{{ option.description }}</ng-option>
            }
          </ng-select>
          <div validationErrors [for]="genderCodeId.control" [errorEmitter]="errorEmitter"></div>
        </div>
        <div class="col-md-6">
          <label inputRequired for="workforce-person-ethnicityCodeId" class="form-label">Ethnicity</label>
          <ng-select
            labelForId="workforce-person-ethnicityCodeId"
            [readonly]="busy()"
            name="ethnicityCodeId"
            #ethnicityCodeId="ngModel"
            [closeOnSelect]="true"
            [multiple]="false"
            [ngModel]="draft().ethnicityCodeId"
            (ngModelChange)="update('ethnicityCodeId', $event)"
          >
            @for (option of codes()?.ethnicityCodes ?? []; track option.ethnicityCodeId) {
              <ng-option [value]="option.ethnicityCodeId">{{ option.description }}</ng-option>
            }
          </ng-select>
          <div validationErrors [for]="ethnicityCodeId.control" [errorEmitter]="errorEmitter"></div>
        </div>
        <div class="col-md-6">
          <label inputRequired for="workforce-person-raceCodeIds" class="form-label"
            >Race (select all that apply)</label
          >
          <ng-select
            labelForId="workforce-person-raceCodeIds"
            [readonly]="busy()"
            name="raceCodeIds"
            #raceCodeIds="ngModel"
            [closeOnSelect]="false"
            [multiple]="true"
            [ngModel]="draft().raceCodeIds"
            (ngModelChange)="update('raceCodeIds', $event)"
          >
            @for (option of codes()?.raceCodes ?? []; track option.raceCodeId) {
              <ng-option [value]="option.raceCodeId">{{ option.description }}</ng-option>
            }
          </ng-select>
          <div validationErrors [for]="raceCodeIds.control" [errorEmitter]="errorEmitter"></div>
        </div>
      </div>
    </fieldset>
    @if (matches(); as result) {
      <div class="mt-3" role="status">
        @if (result.creationBlocked) {
          <p class="alert alert-warning">
            An existing identity was found. Select that person to continue; another person record cannot be created.
          </p>
        } @else {
          <p>
            We found possible matches. Choose the existing person or confirm that you are creating a different person.
          </p>
        }
        @for (person of result.matches; track person.personId) {
          <div class="border rounded p-3 mb-2">
            <strong>{{ person.firstName }} {{ person.middleName }} {{ person.lastName }}</strong> · EIN {{ person.ein }}
            @if (person.isAlreadyActiveInLea) {
              <span class="badge badge-warning ms-2">Already on this roster</span>
            } @else {
              <button type="button" class="btn btn-outline-primary ms-2" [disabled]="busy()" (click)="select(person)">
                Use Existing Person
              </button>
            }
          </div>
        }
        @if (!result.creationBlocked && result.matches.length > 0) {
          <button type="button" class="btn btn-primary" [disabled]="busy()" (click)="save(personForm, true)">
            These Are Different People — Create Person
          </button>
        }
      </div>
    }
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-outline-secondary" [disabled]="busy()" (click)="cancelled.emit()">
      Cancel
    </button>
    <button type="button" class="btn btn-outline-primary" [disabled]="busy()" (click)="back.emit()">
      Back to Search
    </button>
    @if (!matches()?.matches?.length) {
      <button type="submit" class="btn btn-primary" [disabled]="busy() || !codes()">
        {{ busy() ? 'Working…' : 'Save Person and Continue' }}
      </button>
    }
  </div>
</form>
